export const TermsPage = () => {
  return (
    <div className="p-4 prose prose-sm">
      <h1>Terms of Service</h1>

      <div className="section" id="about-terms">
        <h2>About Our Terms</h2>
        <p>
          These terms and conditions of use ("Terms") explain how you may use
          our website, application, and any related services, including our
          platform (collectively, the "Services"). These Terms apply between
          Clue Content Ltd ("we," "us," or "our") and you, the person accessing
          or using the Services ("you" or "your").
        </p>
        <p>
          By using our Services, you agree to be bound by these Terms. If you do
          not agree with any of these Terms, you should stop using the Services
          immediately.
        </p>
        <p>
          If you purchase any subscriptions, goods, services, or digital content
          through our Services, separate terms and conditions may apply.
        </p>
        <p>
          If you would like these Terms in another format (e.g., audio, large
          print, braille), please contact us using the details provided below.
        </p>
      </div>

      <div className="section" id="about-us">
        <h2>About Us</h2>
        <p>
          Clue Content Ltd is a company registered in England and Wales under
          company registration number 1449051. Our registered office is located
          at: 1A The Crescent, Dullingham, Newmarket, Suffolk, CB8 9UY.
        </p>
        <p>
          If you have any questions about the Services, please contact us by:
        </p>
        <ul>
          <li>
            Emailing:{' '}
            <a href="mailto:hello@cluecontent.co.uk">hello@cluecontent.co.uk</a>
          </li>
          <li>Using our in-app support features where available</li>
        </ul>
      </div>

      <div className="section" id="using-services">
        <h2>Using the Services</h2>
        <p>The Services are for your personal and non-commercial use only.</p>

        <div className="sub-section">
          <h3>Conditions of Use:</h3>
          <ul>
            <li>
              You are responsible for all costs incurred in using the Services.
            </li>
            <li>
              We do not guarantee the availability of our Services outside the
              UK. If you access them internationally, you are responsible for
              compliance with local laws.
            </li>
          </ul>

          <h3>Prohibited Use:</h3>
          <p>You agree not to:</p>
          <ul>
            <li>
              Misuse or attack the Services by introducing malicious code, such
              as viruses or trojans.
            </li>
            <li>Attempt unauthorized access to our servers or databases.</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate access to the Services
            for violations of these Terms or applicable laws.
          </p>
        </div>
      </div>

      <div className="section" id="registration">
        <h2>Registration and Account Security</h2>
        <ul>
          <li>
            Use of certain features of the Services requires registration.
          </li>
          <li>
            You must keep your account credentials confidential. Unauthorized
            use of your account must be reported to us immediately.
          </li>
          <li>
            Personal information provided during registration will be handled
            per our Privacy Policy.
          </li>
        </ul>
      </div>

      <div className="section" id="infringing-content">
        <h2>Infringing Content</h2>
        <p>We will:</p>
        <ul>
          <li>
            Remove or disable access to content that violates these Terms or our
            Acceptable Use Policy when notified.
          </li>
          <li>
            Delete accounts engaged in inappropriate or infringing activities.
          </li>
        </ul>
        <p>
          To report content violations, contact us at{' '}
          <a href="mailto:hello@cluecontent.co.uk">hello@cluecontent.co.uk</a>.
        </p>
      </div>

      <div className="section" id="privacy">
        <h2>Your Privacy and Personal Information</h2>
        <p>
          Your privacy is important to us. Please review our Privacy Policy for
          information on how we collect, use, and protect your data.
        </p>
      </div>

      <div className="section" id="intellectual-property">
        <h2>Intellectual Property Rights</h2>
        <ul>
          <li>
            All intellectual property rights in the Services and associated
            content are owned by us or our licensors.
          </li>
          <li>
            You may not copy, modify, or use our intellectual property without
            written permission.
          </li>
        </ul>
      </div>

      <div className="section" id="submitting-information">
        <h2>Submitting Information</h2>
        <p>
          Any non-personal information you submit to the Services may be used by
          us as outlined in our Privacy Policy.
        </p>
      </div>

      <div className="section" id="accuracy">
        <h2>Accuracy and Availability</h2>
        <p>
          While we aim for accuracy, we cannot guarantee the Services will
          always be error-free, up-to-date, or uninterrupted.
        </p>
      </div>

      <div className="section" id="third-party">
        <h2>Third-Party Services</h2>
        <p>
          The Services may include links or integrations with third-party
          platforms, including but not limited to Meta's platforms (e.g.,
          Facebook, Instagram). Use of such platforms is subject to their terms
          of service and policies, including Meta's Platform Terms available at{' '}
          <a href="https://developers.facebook.com/terms/dfc_platform_terms/">
            https://developers.facebook.com/terms/dfc_platform_terms/
          </a>
          .
        </p>
      </div>

      <div className="section" id="liability">
        <h2>Limitation of Liability</h2>
        <p>We are not liable for:</p>
        <ul>
          <li>
            Losses not foreseeable by both parties at the time of the agreement.
          </li>
          <li>Losses not caused by any breach on our part.</li>
          <li>Business losses or non-consumer losses.</li>
        </ul>
        <p>Our liability is limited to the maximum extent permitted by law.</p>
      </div>

      <div className="section" id="force-majeure">
        <h2>Events Beyond Our Control</h2>
        <p>
          We are not liable for delays or failures resulting from events beyond
          our reasonable control, including natural disasters, strikes, or
          technical issues.
        </p>
      </div>

      <div className="section" id="third-party-rights">
        <h2>Rights of Third Parties</h2>
        <p>These Terms do not grant rights to any third parties.</p>
      </div>

      <div className="section" id="variation">
        <h2>Variation</h2>
        <p>
          We may update these Terms from time to time. Continued use of the
          Services indicates your acceptance of updated Terms. It is your
          responsibility to check for changes periodically.
        </p>
      </div>

      <div className="section" id="disputes">
        <h2>Disputes</h2>
        <p>
          We aim to resolve disputes quickly and efficiently. If unresolved,
          alternative dispute resolution (ADR) may be pursued before court
          proceedings. Relevant UK law governs these Terms, and UK courts have
          jurisdiction over disputes.
        </p>
      </div>

      <div className="contact-info">
        <h2>Contact Information</h2>
        <p>
          Clue Content Ltd
          <br />
          1A The Crescent
          <br />
          Dullingham
          <br />
          Newmarket
          <br />
          Suffolk, CB8 9UY
          <br />
          Email:{' '}
          <a href="mailto:hello@cluecontent.co.uk">hello@cluecontent.co.uk</a>
        </p>
      </div>

      <footer className="mt-12 pt-4 border-t border-gray-200 text-center">
        <p>© 2025 Clue Content Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
};
